import { useEffect, useState, useCallback } from 'react';
import { useApi } from '../../../utils/hooks';
import { IFileInfoDto, IFileListResult } from '../../../utils/constants/interfaces';

const useFileManager = () => {
  const api = useApi();

  const [currentDirectory, setCurrentDirectory] = useState<string>('');
  const [files, setFiles] = useState<IFileInfoDto[]>([]);
  const [directories, setDirectories] = useState<string[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string>('');

  const loadDirectory = useCallback(
    async (directoryPath: string = '') => {
      setLoading(true);
      setError('');

      try {
        const endpoint = `/files/list${directoryPath ? `?directoryPath=${encodeURIComponent(directoryPath)}` : ''}`;
        const apiResponse = await api.get<IFileListResult>(endpoint);

        if (apiResponse.isSuccessStatusCode && apiResponse.response) {
          const result = apiResponse.response;
          if (result.succeeded) {
            // Filter files to only show those in the current directory
            const filteredFiles = result.files.filter((file) => {
              // For root directory, only show files that are directly in root (no directory path)
              if (!directoryPath) {
                const isRootFile = !file.directory || file.directory === '';
                return isRootFile;
              }
              // For subdirectories, only show files in the exact current directory
              const isInCurrentDir = file.directory === directoryPath;
              return isInCurrentDir;
            });

            // Filter directories to only show immediate subdirectories
            const filteredDirectories = result.directories.filter((dir) => {
              // For root directory, only show top-level directories (no slashes)
              if (!directoryPath) {
                const isTopLevel = !dir.includes('/');
                return isTopLevel;
              }
              // For subdirectories, only show immediate children
              const expectedParent = directoryPath;
              const dirParts = dir.split('/');
              const parentPath = dirParts.slice(0, -1).join('/');
              const isImmediateChild = parentPath === expectedParent;

              return isImmediateChild;
            });

            setFiles(filteredFiles);
            setDirectories(filteredDirectories);
            setCurrentDirectory(directoryPath);
          } else {
            setError(result.message);
          }
        } else {
          setError('Failed to load directory contents');
        }
      } catch (err) {
        setError('An error occurred while loading files');
      } finally {
        setLoading(false);
      }
    },
    [api]
  );

  const navigateToDirectory = useCallback(
    (directoryPath: string) => {
      void loadDirectory(directoryPath);
    },
    [loadDirectory]
  );

  const navigateUp = useCallback(() => {
    if (currentDirectory) {
      const pathParts = currentDirectory.split('/');
      pathParts.pop();
      const parentPath = pathParts.join('/');
      void loadDirectory(parentPath);
    }
  }, [currentDirectory, loadDirectory]);

  // Load root directory on mount
  useEffect(() => {
    void loadDirectory();
  }, [loadDirectory]);

  return {
    currentDirectory,
    files,
    directories,
    loading,
    error,
    loadDirectory,
    navigateToDirectory,
    navigateUp,
    hasParentDirectory: currentDirectory !== ''
  };
};

export default useFileManager;
